import { QueryClient } from '@tanstack/react-query'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import { getCookie, setCookie } from 'cookies-next'
import type { User as AuthUser } from 'firebase/auth'
import React, { createContext, useEffect, useState } from 'react'
import { useIntercom } from 'react-use-intercom'
import { useLocalStorage } from 'usehooks-ts'
import useAuthUser from '~/src/hooks/useAuthUser'
import useGetTempUserId from '~/src/hooks/useGetTempUserId'
import { createIDBPersister } from '~/src/lib/queryPersister'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      networkMode: 'always', // https://github.com/TanStack/query/discussions/4753
    },
    mutations: {
      networkMode: 'always',
    },
  },
})

const persister = createIDBPersister('reactQuery')

interface IAppContext {
  hash?: string
  authUser: AuthUser | null | undefined
  isAdmin: boolean
  phoneSubmitted: string | undefined
  setPhoneSubmitted: React.Dispatch<React.SetStateAction<string | undefined>>
  isVisionEnabled: boolean
  errorMessage: string | null
  setErrorMessage: React.Dispatch<React.SetStateAction<string | null>>
  loadingAuthUser: boolean | undefined
}

const defaultAppContext: IAppContext = {
  hash: undefined,
  authUser: undefined,
  isAdmin: false,
  phoneSubmitted: undefined,
  setPhoneSubmitted: () => {},
  isVisionEnabled: false,
  errorMessage: null,
  setErrorMessage: () => {},
  loadingAuthUser: undefined,
}

export const AppContext = createContext<IAppContext>(defaultAppContext)

export const useAppContext = (): IAppContext => {
  return React.useContext(AppContext)
}

interface Props extends React.PropsWithChildren {
  hash?: string
}

export const AppProvider: React.FC<Props> = ({ children, hash }) => {
  const { boot } = useIntercom()
  const { authUser, isAdmin, loading: loadingAuthUser } = useAuthUser()
  const [phoneSubmitted, setPhoneSubmitted] = useLocalStorage<
    string | undefined
  >('__dupe_phoneSubmitted', undefined)
  const [isVisionEnabled, setIsVisionEnabled] = useState(false)

  const userId = authUser?.uid
  const { tempUserId } = useGetTempUserId()

  useEffect(() => {
    if (isVisionEnabled) return
    if (!isAdmin) return

    // TODO - temp feature flag during development
    if (isAdmin) {
      setIsVisionEnabled(true)
    }
  }, [isAdmin, isVisionEnabled])

  useEffect(() => {
    if (loadingAuthUser) return

    boot({
      hideDefaultLauncher: true,
      userId: userId || tempUserId,
    })
  }, [boot, tempUserId, userId, loadingAuthUser])

  // if hash is present, save it
  // to the user's recent dupes list in a cookie
  useEffect(() => {
    if (hash) {
      const cookieValue = getCookie('hashes')
      const existingHashes =
        (typeof cookieValue === 'string' ? cookieValue : '')?.split(',') || []

      if (existingHashes.includes(hash)) return
      if (existingHashes.length > 6) {
        existingHashes.pop()
      }

      const newCookieValue = [...new Set([hash, ...existingHashes])].join(',')
      setCookie('hashes', newCookieValue)
    }
  }, [hash])
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        buster: 'v1',
        maxAge: 0, // In future can adjust this to use
      }}
    >
      <AppContext.Provider
        value={{
          hash,
          authUser,
          isAdmin,
          phoneSubmitted,
          setPhoneSubmitted,
          isVisionEnabled,
          errorMessage,
          setErrorMessage,
          loadingAuthUser,
        }}
      >
        {children}
      </AppContext.Provider>
    </PersistQueryClientProvider>
  )
}
